import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { format, isPast, isToday, parseISO, isBefore, isAfter, differenceInMinutes } from 'date-fns';

import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';
import { useShiftStatusService } from '@/hooks/scheduling/useShiftStatusService';
import { ShiftStatusBadge } from '@/components/shifts/status/ShiftStatusBadge';
import { StyleVariant } from '@/lib/scheduling/shift-status-styles';

interface EmployeeShift {
  id: string; // This is the shift ID
  start_time: string; // TIMESTAMPTZ
  end_time: string;   // TIMESTAMPTZ
  actual_arrival_time?: string | null; // Added actual arrival time
  status?: ShiftStatus; // Using unified status system
}



interface EmployeeShiftsTabProps {
  employeeId: string;
  businessId: string;
}

const EmployeeShiftsTab: React.FC<EmployeeShiftsTabProps> = ({ employeeId, businessId }) => {
  const { user } = useAuth();
  const [upcomingShifts, setUpcomingShifts] = useState<EmployeeShift[]>([]);
  const [previousShifts, setPreviousShifts] = useState<EmployeeShift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Timezone-aware shift status service
  const shiftStatusService = useShiftStatusService();

  useEffect(() => {
    const fetchEmployeeShifts = async () => {
      if (!employeeId) {
        setError('Employee ID not provided.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {

        // 2. Fetch shifts for the employee directly from the 'shifts' table
        const { data: shiftsData, error: shiftsError } = await supabase
          .from('shifts')
          .select(`
            id,
            start_time,
            end_time
          `)
          .eq('employee_id', employeeId)
          .order('start_time', { ascending: true });

        if (shiftsError) {
          throw new Error(`Error fetching shifts: ${shiftsError.message}`);
        }

        if (!shiftsData) {
          setUpcomingShifts([]);
          setPreviousShifts([]);
          setLoading(false);
          return;
        }

        // Fetch time logs for the employee for all relevant dates
        const { data: timeLogsData, error: timeLogsError } = await supabase
          .from('time_logs')
          .select('scanned_at')
          .eq('employee_id', employeeId)
          .eq('event_type', 'arrival')
          .order('scanned_at', { ascending: true });

        if (timeLogsError) {
          console.error('Error fetching time logs:', timeLogsError);
          // Do not throw, as shifts should still load even if time logs fail
        }

        const now = new Date();
        const upcoming: EmployeeShift[] = [];
        const previous: EmployeeShift[] = [];

        (shiftsData as EmployeeShift[]).forEach((employeeShift) => {
          const shiftStart = parseISO(employeeShift.start_time);
          const shiftEnd = parseISO(employeeShift.end_time);

          // Find the first arrival log that falls within a reasonable window of the shift start
          const relevantArrivalLog = timeLogsData?.find(log => {
            const scannedAt = parseISO(log.scanned_at);
            // Consider an arrival relevant if it's within 1 hour before or after the shift start
            return isAfter(scannedAt, new Date(shiftStart.getTime() - 60 * 60 * 1000)) &&
                   isBefore(scannedAt, new Date(shiftEnd.getTime() + 60 * 60 * 1000));
          });

          employeeShift.actual_arrival_time = relevantArrivalLog?.scanned_at || null;
          
          // Calculate status using timezone-aware service
          employeeShift.status = shiftStatusService.calculateShiftStatus(
            shiftStart, // Already parsed Date object
            shiftEnd,   // Already parsed Date object
            employeeId, // Employee ID from component props
            employeeShift.actual_arrival_time,
            null // No departure time tracking in this component
          );

          const shiftEndDateTime = parseISO(employeeShift.end_time);
          
          if (isPast(shiftEndDateTime) && !isToday(shiftEndDateTime)) {
            previous.push(employeeShift);
          } else {
            upcoming.push(employeeShift);
          }
        });

        setUpcomingShifts(upcoming);
        setPreviousShifts(previous);

      } catch (err) {
        console.error('Failed to fetch employee shifts:', err);
        setError(err instanceof Error ? err.message : 'Failed to load shifts.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeShifts();
  }, [employeeId, businessId, shiftStatusService]);

  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Loading Shifts...</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Please wait while your shifts are being loaded.</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-500">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium text-red-400">Error Loading Shifts</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-300">{error}</p>
          <p className="text-red-300 mt-2">Please try again later.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Upcoming Shifts</CardTitle>
          <CardDescription>Your scheduled shifts in the future.</CardDescription>
        </CardHeader>
        <CardContent>
          {upcomingShifts.length === 0 ? (
            <p>No upcoming shifts found.</p>
          ) : (
            <ul className="space-y-4">
              {upcomingShifts.map((employeeShift) => (
                <li key={employeeShift.id} className="p-4 border rounded-lg">
                  <p className="text-lg font-semibold">
                    {format(employeeShift.startTime, 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-md">
                    {format(employeeShift.startTime, 'h:mm a')} -{' '}
                    {format(employeeShift.endTime, 'h:mm a')}
                  </p>
                  <div className="mt-2">
                    <ShiftStatusBadge
                      status={employeeShift.calculatedStatus || employeeShift.status || ShiftStatus.PENDING}
                      variant={StyleVariant.COMPACT}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">Previous Shifts</CardTitle>
          <CardDescription>Your past scheduled shifts.</CardDescription>
        </CardHeader>
        <CardContent>
          {previousShifts.length === 0 ? (
            <p>No previous shifts found.</p>
          ) : (
            <ul className="space-y-4">
              {previousShifts.map((employeeShift) => (
                <li key={employeeShift.id} className="p-4 border rounded-lg">
                  <p className="text-lg font-semibold">
                    {format(employeeShift.startTime, 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-md">
                    {format(employeeShift.startTime, 'h:mm a')} -{' '}
                    {format(employeeShift.endTime, 'h:mm a')}
                  </p>
                  <div className="mt-2">
                    <ShiftStatusBadge
                      status={employeeShift.calculatedStatus || employeeShift.status || ShiftStatus.PENDING}
                      variant={StyleVariant.COMPACT}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeShiftsTab;
